import {DataTable} from "../DataTable/DataTable";
import {InvoiceEntity, TicketCodeEntity, TransportLineItemEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Anchor, Group, Text} from "@mantine/core";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useInvoiceDataTableFilter} from "./useInvoiceDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DataTableSumFooter} from "../DataTable/footer/DataTableSumFooter/DataTableSumFooter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode"
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency"
import {TransportCategoryCell} from "../cells/TransportCategoryCell/TransportCategoryCell";
import {HaulMethodCell} from "../cells/HaulMethodCell/HaulMethodCell";
import {Link} from "react-router-dom";
import {IconCircleCheck, IconCircleDashed, IconDownload} from "@tabler/icons-react";
import {useDownloadInvoicePdf} from "../../../hooks/useDownloadInvoicePdf";
import {useCallback, useMemo} from "react";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";

export const CodingIcon = (props: { coded?: boolean, codes?: TicketCodeEntity[] }) => {
  const {coded, codes} = props

  const color = useMemo(() => {
    if (typeof coded !== 'undefined') {
      if (coded || (codes && codes.length > 1)) {
        return "green"
      } else {
        return "yellow"
      }
    }

    return undefined
  }, [coded, codes])

  const tooltip = useMemo(() => {
    if (typeof coded !== 'undefined') {
      if (codes && codes.length > 1) {
        return "Multi Coded"
      } else if (coded) {
        return "Coded"
      } else {
        return "Partially Coded"
      }
    }

    return undefined
  }, [coded, codes])

  if (typeof coded === 'undefined') {
    return null
  }

  return (
    <Text color={color}>
      <Tooltip label={tooltip}>
        <IconCircleCheck size={14}/>
      </Tooltip>
    </Text>
  )
}

const EMPTY_ARRAY: InvoiceEntity[] = []

export type InvoiceDataTableProps = EntityDataTableProps<InvoiceEntity>

export const InvoiceDataTable = (props: InvoiceDataTableProps) => {
  const {
    records: invoices = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props;

  const {baseQueryApi} = useApp();
  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useInvoiceDataTableFilter({
    records: invoices
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'invoiceNumber', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    downloadInvoicePdf,
  } = useDownloadInvoicePdf({baseQueryApi})

  const onDownloadClick = useCallback((invoice: InvoiceEntity) => {
    downloadInvoicePdf(invoice, undefined)
  }, [downloadInvoicePdf])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          ...getColumnFilterProps(filterConfig, "invoiceNumber"),
          title: t("table.entity.invoice.column.name", "Name"),
          accessor: "invoiceNumber",
          width: 100,
          sortable: true,
          render: (record) => (
            <Group spacing={4} position="left" noWrap>
              {record.disputed &&
                <Text color="red">
                  <IconCircleDashed size={14}/>
                </Text>
              }
              <CodingIcon coded={record.coded} codes={record.codes}/>
              <Anchor component={Link}
                      to={`profile?id=${record.id}` + (record.dbName ? `&database=${record.dbName}` : ``)}>
                {record.invoiceNumber}
              </Anchor>
            </Group>
          )
        },
        {
          title: t("table.entity.invoice.column.date", "Date"),
          accessor: "createDttm",
          width: 100,
          sortable: true,
          render: ({createDttm}) => {
            return <FormattedDateTime date={createDttm} displayTime={false}/>
          },
        },
        {
          ...getColumnFilterProps(filterConfig, "status"),
          title: t("table.entity.invoice.column.status", "Status"),
          accessor: "status.code",
          width: 100,
          sortable: true,
          render: ({status}) => (
            <FormattedDatabaseCode value={status}/>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "category"),
          title: t("table.entity.invoice.column.category", "Category"),
          accessor: "category.code",
          width: 100,
          sortable: true,
          render: ({category}) => (
            <TransportCategoryCell category={category}/>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "method"),
          title: t("table.entity.invoice.column.method", "Method"),
          accessor: "method.code",
          width: 100,
          sortable: true,
          render: ({method}) => (
            <HaulMethodCell haulMethod={method}/>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "operator"),
          title: t("table.entity.invoice.column.operator", "Operator"),
          accessor: "operatorName",
          width: 100,
          sortable: true,
          render: ({operatorName}) => (
            <DefaultText value={operatorName}/>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "vendor"),
          title: t("table.entity.invoice.column.vendor", "Vendor"),
          accessor: "vendorName",
          width: 100,
          sortable: true,
          render: ({vendorName}) => (
            <DefaultText value={vendorName}/>
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "secondaryVendor"),
          title: t("table.entity.invoice.column.secondaryvendor", "Secondary Vendor"),
          accessor: "secondaryVendorName",
          width: 100,
          sortable: true,
          render: ({secondaryVendorId, secondaryVendorName}) => (
            secondaryVendorId ? <DefaultText value={secondaryVendorName}/> : undefined
          ),
        },
        {
          title: t("table.entity.invoice.column.cost", "Cost"),
          accessor: "totalCost",
          width: 100,
          sortable: true,
          render: ({totalCost}) => {
            return <FormattedCurrency value={totalCost}/>
          },
          footer: (
            <DataTableSumFooter
              value={{
                number: filteredRecords.reduce((prev, curr) => prev + (curr.totalCost ?? 0), 0)
              }}
            />
          ),
        },
        {
          title: '',
          accessor: 'actions',
          width: 50,
          render: (record) => {
            return (
              <Group spacing={4} position="right" noWrap>
                <ActionIcon variant="transparent" onClick={() => onDownloadClick(record)}>
                  <IconDownload size={14}/>
                </ActionIcon>
              </Group>
            )
          }
        }
      ]}
    />
  );
};
